import React, { useState, useEffect } from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { BellOutlined, CloseOutlined } from '@ant-design/icons';
import { setNotificationCallback } from '../../services/socketService';
import { usersAPI } from '../../services/apiService';
import './NotificationCenter.css';

const NOTIFICATION_LIMIT = 50;

function getCurrentUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.id || null;
  } catch {
    return null;
  }
}

function mapServerNotification(item) {
  return {
    id: item.id,
    type: item.type,
    title: item.title,
    message: item.message,
    metadata: item.metadata,
    created_at: item.created_at,
    read: Boolean(item.is_read || item.read),
  };
}

function mergeNotifications(lists) {
  const byId = new Map();
  lists.flat().forEach((item) => {
    if (!item?.id) return;
    const prev = byId.get(item.id);
    if (!prev) {
      byId.set(item.id, item);
      return;
    }
    byId.set(item.id, {
      ...prev,
      ...item,
      read: Boolean(prev.read || item.read),
    });
  });

  return Array.from(byId.values())
    .sort((a, b) => new Date(b.created_at || b.timestamp || 0) - new Date(a.created_at || a.timestamp || 0))
    .slice(0, NOTIFICATION_LIMIT);
}

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);

  const replaceNotifications = (next) => {
    setNotifications(next);
    setUnreadCount(next.filter((item) => !item.read).length);
  };

  useEffect(() => {
    let cancelled = false;

    const loadServerNotifications = () => {
      const userId = getCurrentUserId();
      if (!userId) return;
      usersAPI.notificationsAPI.getHistory({ userId, limit: NOTIFICATION_LIMIT })
        .then((data) => {
          if (cancelled) return;
          const serverItems = (data.notifications || []).map(mapServerNotification);
          setNotifications((prev) => {
            const next = mergeNotifications([serverItems, prev]);
            setUnreadCount(next.filter((item) => !item.read).length);
            return next;
          });
        })
        .catch((err) => {
          console.error('Error loading notifications:', err);
        });
    };

    setNotificationCallback((notificationData) => {
      setNotifications((prev) => {
        const next = mergeNotifications([[
          { ...notificationData, read: Boolean(notificationData.read) },
        ], prev]);
        setUnreadCount(next.filter((item) => !item.read).length);
        return next;
      });
    });

    const savedNotifications = localStorage.getItem('notifications');
    if (savedNotifications) {
      try {
        const parsed = JSON.parse(savedNotifications);
        if (Array.isArray(parsed) && parsed.length) {
          replaceNotifications(parsed);
        }
      } catch (e) {
        console.error('Error loading notifications:', e);
      }
    }

    loadServerNotifications();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadServerNotifications();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Сохраняем уведомления в localStorage с debounce
  useEffect(() => {
    if (notifications.length > 0) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem('notifications', JSON.stringify(notifications));
      }, 500); // Сохраняем через 500ms после последнего изменения
      
      return () => clearTimeout(timeoutId);
    }
  }, [notifications]);

  const markAsRead = (notificationId) => {
    setNotifications((prev) => {
      const next = prev.map((n) => (
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(next.filter((item) => !item.read).length);
      return next;
    });
    usersAPI.notificationsAPI.markAsRead(notificationId).catch((err) => {
      console.error('Error marking notification as read:', err);
    });
  };

  const markAllAsRead = () => {
    const unreadIds = notifications.filter((item) => !item.read).map((item) => item.id);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    unreadIds.forEach((id) => {
      usersAPI.notificationsAPI.markAsRead(id).catch((err) => {
        console.error('Error marking notification as read:', err);
      });
    });
  };

  const removeNotification = (notificationId) => {
    setNotifications((prev) => {
      const filtered = prev.filter((n) => n.id !== notificationId);
      const unread = filtered.filter((n) => !n.read).length;
      setUnreadCount(unread);
      return filtered;
    });
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem('notifications');
  };

  const getTypeColor = (type) => {
    const colors = {
      info: '#1890ff',
      success: '#52c41a',
      warning: '#faad14',
      error: '#ff4d4f',
    };
    return colors[type] || colors.info;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (minutes < 1) return 'только что';
      if (minutes < 60) return `${minutes} мин назад`;
      if (hours < 24) return `${hours} ч назад`;
      if (days < 7) return `${days} дн назад`;
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="notification-center">
      <Button
        variant="outline-primary"
        className="notification-button"
        onClick={() => setShowPanel(!showPanel)}
        style={{
          position: 'relative',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BellOutlined style={{ fontSize: '18px' }} />
        {unreadCount > 0 && (
          <Badge
            bg="danger"
            style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              fontSize: '10px',
              minWidth: '18px',
              height: '18px',
              padding: '0 4px',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      {showPanel && (
        <Card
          className="notification-panel"
          style={{
            position: 'fixed',
            top: '60px',
            right: '20px',
            width: '400px',
            maxHeight: '600px',
            zIndex: 1050,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <Card.Header className="d-flex justify-content-between align-items-center p-2">
            <div>
              <strong style={{ fontSize: '14px' }}>Уведомления</strong>
              {unreadCount > 0 && (
                <Badge bg="primary" className="ms-2" style={{ fontSize: '10px' }}>
                  {unreadCount} новых
                </Badge>
              )}
            </div>
            <div className="d-flex gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={markAllAsRead}
                  style={{ fontSize: '11px', padding: '2px 6px' }}
                >
                  Прочитать все
                </Button>
              )}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={clearAll}
                style={{ fontSize: '11px', padding: '2px 6px' }}
              >
                Очистить
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowPanel(false)}
                style={{ fontSize: '11px', padding: '2px 6px' }}
              >
                <CloseOutlined />
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0" style={{ overflowY: 'auto', maxHeight: '500px' }}>
            {notifications.length === 0 ? (
              <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>
                Нет уведомлений
              </div>
            ) : (
              <div className="notification-list">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`notification-item ${!notif.read ? 'unread' : ''}`}
                    onClick={() => !notif.read && markAsRead(notif.id)}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e0e0e0',
                      cursor: !notif.read ? 'pointer' : 'default',
                      backgroundColor: !notif.read ? '#f0f7ff' : 'white',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ flex: 1 }}>
                        <div className="d-flex align-items-center mb-1">
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: getTypeColor(notif.type),
                              marginRight: '8px',
                            }}
                          />
                          <strong style={{ fontSize: '13px' }}>{notif.title || 'Уведомление'}</strong>
                          {!notif.read && (
                            <Badge bg="primary" className="ms-2" style={{ fontSize: '9px' }}>
                              Новое
                            </Badge>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginLeft: '16px' }}>
                          {notif.message || ''}
                        </div>
                        <div
                          style={{
                            fontSize: '10px',
                            color: '#999',
                            marginTop: '4px',
                            marginLeft: '16px',
                          }}
                        >
                          {formatDate(notif.created_at || notif.timestamp)}
                        </div>
                      </div>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notif.id);
                        }}
                        style={{
                          padding: '0',
                          color: '#999',
                          fontSize: '12px',
                          minWidth: 'auto',
                        }}
                      >
                        <CloseOutlined />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default NotificationCenter;

